import { User } from 'firebase/auth'
import JSZip from 'jszip'
import { SalaryReportRow } from '@/components/reports/reportConfig'
import { DriverMaster } from '@/types/driverMaster'
import { Earning } from '@/types/earnings'
import { Compensation } from '@/types/compensation'
import { FleetSettings } from '@/types'
import { SalarySpecificationService } from './salarySpecificationService'

/**
 * Service for generating multiple salary specification PDFs in bulk
 * and packaging them into a ZIP file for download
 */
export class BulkSalarySpecificationService {
  /**
   * Generate salary specification PDFs for multiple drivers and download as ZIP
   *
   * @param user - Firebase user (for loading company info)
   * @param reportRows - Array of salary report rows (one per driver)
   * @param period - Period string (e.g., "2025-01")
   * @param settings - Fleet settings
   * @param allEarnings - All earnings (will be filtered per driver)
   * @param allCompensations - All compensations (will be filtered per driver)
   * @param driverMasters - Array of all driver masters
   * @returns Number of successfully generated PDFs
   */
  static async generateBulkPDFs(
    user: User,
    reportRows: SalaryReportRow[],
    period: string,
    settings: FleetSettings,
    allEarnings: Earning[],
    allCompensations: Compensation[],
    driverMasters: DriverMaster[],
    includeCompanyInFilename?: boolean
  ): Promise<number> {
    if (!reportRows || reportRows.length === 0) {
      throw new Error('No drivers to generate PDFs for')
    }

    const zip = new JSZip()
    let successCount = 0

    // Process each driver
    for (const row of reportRows) {
      try {
        // Find the driver master
        const driver = driverMasters.find(d => d.id === row.driverId)
        if (!driver) {
          console.warn(`Driver not found for ID: ${row.driverId}`)
          continue
        }

        // Filter earnings and compensations for this driver and period
        const driverEarnings = allEarnings.filter(
          e => e.driverId === row.driverId && e.period === period
        )
        const driverCompensations = allCompensations.filter(
          c => c.driverId === row.driverId && c.period === period
        )

        // Prepare data for PDF
        const specData = await SalarySpecificationService.prepareSalarySpecificationData(
          user,
          driver,
          period,
          driverEarnings,
          driverCompensations,
          row,
          settings
        )

        // Validate data
        const validation = SalarySpecificationService.validateSalarySpecificationData(specData)
        if (!validation.isValid) {
          console.warn(`Skipping ${driver.primaryName}: ${validation.errors.join(', ')}`)
          continue
        }

        // Format period for display
        const formattedPeriod = SalarySpecificationService.formatPeriodForDisplay(period)

        // Generate PDF blob using centralized service (checks for locked data first)
        const blob = await SalarySpecificationService.generatePdfBlob(user, specData, formattedPeriod)

        // Generate filename
        const filename = SalarySpecificationService.generateFilename(
          driver.primaryName,
          period,
          includeCompanyInFilename ? specData.companyInfo?.name : undefined
        )

        // Add to ZIP
        zip.file(filename, blob)
        successCount++
      } catch (error) {
        console.error(`Failed to generate PDF for driver ${row.driverName}:`, error)
        // Continue with next driver
      }
    }

    // Check if we have any PDFs to download
    if (successCount === 0) {
      throw new Error('Kunde inte generera några lönespecifikationer')
    }

    // Generate ZIP file
    const zipBlob = await zip.generateAsync({ type: 'blob' })

    // Generate ZIP filename
    const zipFilename = this.generateZipFilename(period)

    // Trigger download
    this.downloadZip(zipBlob, zipFilename)

    return successCount
  }

  /**
   * Generate filename for the ZIP archive
   */
  private static generateZipFilename(period: string): string {
    const formattedPeriod = period.replace(/-/g, '')
    return `lonespecifikationer_${formattedPeriod}.zip`
  }

  /**
   * Trigger browser download of the ZIP file
   */
  private static downloadZip(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }
}
